import { useQuery } from '@apollo/client';
import { InstallDesktop } from '@mui/icons-material';
import { Box, Button, DialogActions, DialogContent, MenuItem, Select } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogTitle } from '~/components/dialogs/components';
import { LoadingPane } from '~/components/loading-pane';
import { DeviceCommandKind } from '~/generated/graphql';
import type { Command } from '../../show/lib';
import { WebosFirmwareDialogDocument } from './WebosFirmwareDialog.generated';

interface WebosFirmwareDialogProps {
  deviceId: number;
  handleClick: (command: Command) => Promise<void>;
  onClose: () => void;
}

export const WebosFirmwareDialog = ({
  deviceId,
  handleClick,
  onClose,
}: WebosFirmwareDialogProps) => {
  const { t } = useTranslation();
  const { data, loading } = useQuery(WebosFirmwareDialogDocument, {
    variables: { deviceId },
  });
  const [version, setVersion] = useState('');

  const onSubmit = async () => {
    onClose();
    await handleClick({
      command: DeviceCommandKind.WebosFirmware,
      description: t('pages.devices.dialogs.firmware.description'),
      Icon: InstallDesktop,
      title: '',
      payload: { version },
    });
  };

  const currentVersion =
    data?.device?.metadata &&
    'firmwareVersion' in data.device.metadata &&
    typeof data.device.metadata.firmwareVersion === 'string'
      ? data.device.metadata.firmwareVersion
      : undefined;

  const noneCompatible = !data?.device?.webosFirmwares.some(
    (firmwareVersion) => firmwareVersion.version !== currentVersion,
  );

  return (
    <Dialog fullWidth maxWidth="xs" onClose={onClose} open>
      <DialogTitle onClose={onClose}>{t('pages.devices.dialogs.firmware.title')}</DialogTitle>
      <DialogContent sx={{ fontSize: '1rem' }}>
        <LoadingPane in={loading && !data}>
          {data?.device && (
            <>
              <Box sx={{ mb: 2 }}>
                {t('pages.devices.dialogs.firmware.current_version')} <b>{currentVersion}</b>.
              </Box>
              {noneCompatible ? (
                <Box sx={{}}>{t('pages.devices.dialogs.firmware.no_compatible')}</Box>
              ) : (
                <Select<string>
                  displayEmpty
                  onChange={(event) => setVersion(event.target.value)}
                  sx={{ fontSize: 'inherit' }}
                  value={version}
                >
                  <MenuItem disabled value="">
                    {t('pages.devices.dialogs.firmware.select_version')}
                  </MenuItem>
                  {data.device.webosFirmwares.map((firmwareVersion) => (
                    <MenuItem
                      disabled={firmwareVersion.version === currentVersion}
                      key={firmwareVersion.version}
                      value={firmwareVersion.version}
                    >
                      {firmwareVersion.version}
                    </MenuItem>
                  ))}
                </Select>
              )}
            </>
          )}
        </LoadingPane>
      </DialogContent>
      <DialogActions>
        <Button disabled={loading} onClick={onClose} variant="outlined">
          {t('common.actions.cancel')}
        </Button>

        <Button
          color="primary"
          disabled={loading || noneCompatible || version === ''}
          onClick={onSubmit}
          startIcon={<InstallDesktop />}
          variant="contained"
        >
          {t('pages.devices.dialogs.firmware.install')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
